import {
  ActionError,
  Button,
  EvolutionChart,
  Field,
  FilterSegments,
  Kpi,
  KpiGrid,
  Select,
  Skeleton,
  formatRelative,
  titleCase,
} from '@westhetics/ui'
import { useState } from 'react'
import { usePractitionerOptions } from '@/features/anamneses/api'
import { errorMessage } from '@/lib/errors'
import { PERIODS, type Period, type PeriodResult as PeriodData } from './api'
import {
  FOOTNOTE,
  METRICS,
  PERIOD_LABELS,
  PERIOD_RANGE_LABELS,
  SIGNATURES_NOTE,
  billedLine,
  chartLabel,
  deltaLabel,
  generatedLine,
  money,
  type MetricKey,
} from './labels'

/**
 * "Resultado do período" (docs/areas/dashboard.md §3; RN-DSH-04 a 08; T32): o seletor de período
 * que muda os números de verdade, o filtro de prestador, o número-herói, a grade de KPIs em fio, os
 * contadores de contrato, o movimento com a comparação e a evolução. Enquanto a área de assinaturas
 * não existe, o número-herói é o valor gerado em contratos (P-DSH-15).
 */

export function PeriodResult({
  period,
  onPeriodChange,
  practitionerId,
  onPractitionerChange,
  data,
  isPending,
  error,
  onRetry,
  onExport,
  isExporting,
}: {
  period: Period
  onPeriodChange: (period: Period) => void
  practitionerId: string | null
  onPractitionerChange: (id: string | null) => void
  data: PeriodData | undefined
  isPending: boolean
  error: unknown
  onRetry: () => void
  onExport: () => void
  isExporting: boolean
}) {
  const practitioners = usePractitionerOptions()
  const [metric, setMetric] = useState<MetricKey>('generatedCents')
  const current = METRICS.find((m) => m.key === metric)!
  const rangeLabel = data?.period.label ?? PERIOD_RANGE_LABELS[period]
  // O instante em que a tela abriu: "calculado há 3 minutos" só quando o cálculo passou de 1 minuto.
  const [openedAt] = useState(() => Date.now())
  const calculatedAgo = data ? openedAt - new Date(data.calculatedAt).getTime() : 0

  return (
    <section aria-label="Resultado do período" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-3">
          <h2 className="eyebrow">Resultado · {rangeLabel}</h2>
          <FilterSegments
            label="Período"
            value={period}
            onChange={onPeriodChange}
            options={PERIODS.map((p) => ({ value: p, label: PERIOD_LABELS[p] }))}
          />
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Prestador" className="min-w-[220px]">
            <Select
              value={practitionerId ?? ''}
              onChange={(e) => onPractitionerChange(e.target.value || null)}
              disabled={practitioners.isPending}
            >
              <option value="">Todos os prestadores</option>
              {(practitioners.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {titleCase(p.name)}
                </option>
              ))}
            </Select>
          </Field>
          <Button
            variant="outline"
            size="sm"
            loading={isExporting}
            loadingLabel="Exportando…"
            onClick={onExport}
          >
            Exportar CSV
          </Button>
        </div>
      </div>

      {error ? (
        <ActionError
          title="Não conseguimos calcular o período"
          description={
            data
              ? `${errorMessage(error)} Os números de ${formatRelative(new Date(data.calculatedAt))} continuam abaixo.`
              : errorMessage(error)
          }
          actions={
            <Button variant="outline" size="sm" onClick={onRetry}>
              Tentar de novo
            </Button>
          }
        />
      ) : null}

      {!data ? (
        isPending ? (
          <div role="status" aria-label="Calculando o período" className="flex flex-col gap-3">
            <Skeleton className="h-[96px] w-full" />
            <Skeleton className="h-[120px] w-full" />
            <Skeleton className="h-[200px] w-full" />
          </div>
        ) : null
      ) : (
        <>
          {data.signaturesAvailable ? (
            <Kpi
              hero
              label="Faturamento real"
              value={money(data.netRevenueCents)}
              note={data.billedAppointments === 0 ? data.note : billedLine(data)}
            />
          ) : (
            <Kpi
              hero
              label="Valor gerado em contratos"
              value={money(data.generated.amountCents)}
              note={
                data.generated.count === 0
                  ? data.note
                  : `${generatedLine(data)} · ${deltaLabel(data.generated.amountCents, data.previous.generated.amountCents)}`
              }
            />
          )}

          <KpiGrid>
            <Kpi
              label="Ticket médio"
              value={money(data.averageAppointmentValueCents)}
              note={rangeLabel}
            />
            <Kpi label="Pacientes atendidos" value={data.patientsServed} note={rangeLabel} />
            <Kpi
              label="Faturamento bruto"
              value={money(data.grossRevenueCents)}
              note="antes de ressarcimentos"
            />
            <Kpi
              label="Faturamento real"
              value={money(data.netRevenueCents)}
              note={data.signaturesAvailable ? 'bruto menos ressarcimentos' : SIGNATURES_NOTE}
            />
          </KpiGrid>

          <KpiGrid>
            <Kpi label="Gerados" value={data.contracts.generated} note="contratos no período" />
            <Kpi label="Assinados" value={data.contracts.signed} note="por todas as partes" />
            <Kpi
              label="Sem assinatura"
              value={data.contracts.unsigned}
              tone={data.contracts.unsigned > 0 ? 'critical' : 'default'}
              note="gerados, enviados ou vencidos"
            />
          </KpiGrid>

          <KpiGrid>
            <Kpi
              label="Pacientes novos"
              value={data.movement.newPatients}
              note={deltaLabel(data.movement.newPatients, data.previous.movement.newPatients)}
            />
            <Kpi
              label="Anamneses respondidas"
              value={data.movement.anamnesesCompleted}
              note={deltaLabel(
                data.movement.anamnesesCompleted,
                data.previous.movement.anamnesesCompleted,
              )}
            />
            <Kpi
              label="Documentos gerados"
              value={data.movement.documentsGenerated}
              note={deltaLabel(
                data.movement.documentsGenerated,
                data.previous.movement.documentsGenerated,
              )}
            />
            <Kpi
              label="Atendimentos gerados"
              value={data.generated.count}
              note={deltaLabel(data.generated.count, data.previous.generated.count)}
            />
          </KpiGrid>

          <div className="border-hairline bg-surface flex flex-col gap-4 border px-5 py-[18px]">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="eyebrow">Evolução · {rangeLabel}</h3>
              <FilterSegments
                label="Número da evolução"
                value={metric}
                onChange={setMetric}
                options={METRICS.filter(
                  (m) => data.signaturesAvailable || m.key !== 'netRevenueCents',
                ).map((m) => ({
                  value: m.key,
                  label: m.label,
                }))}
              />
            </div>
            <EvolutionChart
              label={chartLabel(current, data.period.bucket)}
              points={data.series.map((p) => ({
                label: p.label,
                value: p[metric],
                previous: null,
              }))}
              format={current.format}
              emptyNote="Sem movimento no período."
              maxAxisLabels={data.period.bucket === 'day' && data.series.length > 10 ? 6 : 8}
            />
            <p className="font-body text-secondary text-[12px] leading-[1.6] font-light">
              {current.label} no período: {current.format(current.total(data))} · período anterior:{' '}
              {current.format(current.total(data.previous))} ·{' '}
              {deltaLabel(current.total(data), current.total(data.previous))}
            </p>
          </div>

          <p className="font-body text-label text-[11px] leading-[1.6] font-light">
            {data.signaturesAvailable ? FOOTNOTE : `${FOOTNOTE} ${SIGNATURES_NOTE}`}
            {calculatedAgo > 60_000
              ? ` Calculado ${formatRelative(new Date(data.calculatedAt))}.`
              : ''}
          </p>
        </>
      )}
    </section>
  )
}
